import { Brain, MessageCircle, Users, DollarSign, Code } from "lucide-react";

const features = [
  {
    id: "ingest",
    title: "Add your knowledge",
    shortTitle: "Add knowledge",
    description: "Add your expertise and insights to build a Posit that captures your unique knowledge.",
    icon: Brain,
    imageAlt: "Knowledge ingestion visualization"
  },
  {
    id: "approach",
    title: "Responds with your experience",
    shortTitle: "Your experience",
    description: "Posit doesn't just respond with facts, it responds the way you would, incorporating your experience.",
    icon: MessageCircle,
    imageAlt: "Personalized response visualization"
  },
  {
    id: "access",
    title: "Access your Posit",
    shortTitle: "Access your Posit",
    description: "Keep your Posit private for personal use or share it with those who need your knowledge.",
    icon: Users,
    imageAlt: "Access control visualization"
  },
  {
    id: "monetize",
    title: "Monetize your Posit",
    shortTitle: "Monetize your Posit",
    description: "Set a monthly subscription for access to your Posit and earn income from your experience.",
    icon: DollarSign,
    imageAlt: "Monetization visualization"
  },
  {
    id: "integrate",
    title: "Posit in the loop",
    shortTitle: "Posit in the loop",
    description: "Add Posit to your agentic workflows as a 'human' in the loop when your experience is needed.",
    icon: Code,
    imageAlt: "API integration visualization"
  }
];

const FeatureShowcase = () => {
  // Static image data - always show the first feature's visual
  const staticImageData = features[0];

  return (
    <section id="product" className="relative pt-12 pb-16 md:pt-20 md:pb-24 overflow-visible">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-center">
            
            {/* Mobile: Image First, Desktop: Tabs First */}
            <div className="lg:hidden w-full order-1 flex items-center justify-center pb-4">
              <div className="w-full max-w-md p-6">
                <div className="relative">
                  <div className="aspect-square bg-gradient-to-br from-primary/20 to-primary/10 rounded-3xl flex items-center justify-center border-2 border-primary/20 shadow-xl">
                    <div className="text-center p-6">
                      <staticImageData.icon className="h-20 w-20 text-primary mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-foreground mb-3 leading-tight">
                        {staticImageData.shortTitle}
                      </h3>
                      <p className="text-base text-foreground/70 font-medium">
                        {staticImageData.imageAlt}
                      </p>
                    </div>
                  </div>
                  
                  {/* Decorative elements */}
                  <div className="absolute -top-3 -right-3 w-5 h-5 bg-primary rounded-full opacity-80 animate-pulse"></div>
                  <div className="absolute -bottom-3 -left-3 w-7 h-7 bg-primary/40 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                </div>
              </div>
            </div>

            {/* Left Side - Tabs (Desktop) / Bottom (Mobile) */}
            <div className="lg:w-1/2 w-full order-2 lg:order-1 flex flex-col justify-center">
              
              <div className="space-y-2 lg:space-y-3">
                {features.map((feature, index) => (
                  <div
                    key={feature.id}
                    className="p-2 lg:p-4 rounded-lg border-2 bg-background/50 border-border"
                  >
                    <div className="flex items-center gap-2 lg:gap-3">
                      <div className="w-7 h-7 lg:w-8 lg:h-8 rounded-md flex items-center justify-center bg-primary/20 text-primary flex-shrink-0">
                        <feature.icon className="h-3 w-3 lg:h-4 lg:w-4" />
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="font-semibold text-xs lg:text-sm leading-tight text-foreground mb-1">
                          {feature.title}
                        </h3>
                        <p className="text-xs text-foreground/70">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Content (Desktop Only) */}
            <div className="hidden lg:flex lg:w-1/2 items-center justify-center order-2">
              <div className="w-full max-w-lg p-8">
                <div className="relative">
                  <div className="aspect-square bg-gradient-to-br from-primary/20 to-primary/10 rounded-3xl flex items-center justify-center border-2 border-primary/20 shadow-2xl">
                    <div className="text-center p-8">
                      <staticImageData.icon className="h-24 w-24 text-primary mx-auto mb-6" />
                      <h3 className="text-2xl font-bold text-foreground mb-4 leading-tight">
                        {staticImageData.shortTitle}
                      </h3>
                      <p className="text-base text-foreground/70 font-medium">
                        {staticImageData.imageAlt}
                      </p>
                    </div>
                  </div>
                  
                  {/* Decorative elements */}
                  <div className="absolute -top-3 -right-3 w-6 h-6 bg-primary rounded-full opacity-80 animate-pulse"></div>
                  <div className="absolute -bottom-3 -left-3 w-8 h-8 bg-primary/40 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                  <div className="absolute top-1/2 -left-6 w-4 h-4 bg-primary/60 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </section>
  );
};

export default FeatureShowcase; 